<template>
  <div class="month-view">
    <!-- Weekday Headers -->
    <div class="weekday-headers">
      <div
        v-for="day in weekDays"
        :key="day"
        class="weekday-header"
      >
        {{ day }}
      </div>
    </div>

    <!-- Calendar Days -->
    <div class="month-grid">
      <div
        v-for="(day, index) in calendarDays"
        :key="index"
        class="calendar-day"
        :class="{ 'other-month': !day.isCurrentMonth }"
        @click="$emit('dayClick', day)"
      >
        <div class="day-header">
          <span
            class="day-number"
            :class="{
              'text-muted': !day.isCurrentMonth,
              'today': day.isToday,
            }"
          >
            {{ day.date }}
          </span>
          <span
            v-if="getEventsForDay(day).length > 0"
            class="event-count"
          >
            {{ getEventsForDay(day).length }}
          </span>
        </div>

        <!-- Events -->
        <div class="day-events">
          <div
            v-for="event in getEventsForDay(day).slice(0, 3)"
            :key="event.id"
            class="event-pill"
            :style="{ backgroundColor: getStatusColor(event.status) }"
            @click.stop="$emit('eventClick', event)"
          >
            {{ event.cliente_id }} - {{ formatCliente(event.cliente) || event.observacoes }}
          </div>
          <div
            v-if="getEventsForDay(day).length > 3"
            :id="'more-link-' + day.dateKey"
            class="more-link"
            @click.stop
          >
            +{{ getEventsForDay(day).length - 3 }} mais
          </div>

          <!-- Popover for showing all events of the day -->
          <b-popover
            v-if="getEventsForDay(day).length > 3"
            :target="'more-link-' + day.dateKey"
            triggers="click"
            placement="bottom"
            custom-class="calendar-day-popover"
            boundary="viewport"
          >
            <template #title>
              <div class="popover-header-title">
                <span class="popover-date">{{ formatFullDate(day.fullDate) }}</span>
                <span class="popover-count">{{ getEventsForDay(day).length }} agendamentos</span>
              </div>
            </template>
            <div class="popover-body-content">
              <div class="popover-events-list">
                <div
                  v-for="event in getEventsForDay(day)"
                  :key="event.id"
                  class="popover-event-pill"
                  :style="{ backgroundColor: getStatusColor(event.status) }"
                  @click.stop="$emit('eventClick', event)"
                >
                  {{ event.cliente_id }} - {{ formatCliente(event.cliente) || event.observacoes }}
                </div>
              </div>
              <div class="popover-footer">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  block
                  @click.stop="$emit('openDayView', day)"
                >
                  Ver dia completo
                </b-button>
              </div>
            </div>
          </b-popover>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BPopover, BButton } from 'bootstrap-vue'
import { getStatusColor, formatCliente, formatFullDate } from '../calendar-utils'

export default {
  name: 'CalendarMonthView',
  components: {
    BPopover,
    BButton,
  },
  props: {
    weekDays: { type: Array, required: true },
    calendarDays: { type: Array, required: true },
    getEventsForDay: { type: Function, required: true },
  },
  methods: {
    getStatusColor,
    formatCliente,
    formatFullDate,
  },
}
</script>

<style scoped>
.month-view {
  background: #fff;
  min-height: 500px;
}

.weekday-headers {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-bottom: 1px solid #dadce0;
  background: #fff;
}

.weekday-header {
  padding: 8px 12px;
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #70757a;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.calendar-day {
  height: 115px;
  overflow: hidden;
  cursor: pointer;
  border-bottom: 1px solid #dadce0;
  border-right: 1px solid #dadce0;
  background: #fff;
  padding: 6px;
  transition: background-color 0.15s;
}

.calendar-day:nth-child(7n) {
  border-right: 0;
}

.calendar-day:hover {
  background: #f8fafd;
}

.calendar-day.other-month {
  background: #f8fafd;
}

.day-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 4px;
}

.day-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 400;
  color: #3c4043;
}

.day-number.text-muted {
  color: #9aa0a6;
}

.day-number.today {
  background: #1a73e8;
  color: #fff;
  font-weight: 500;
}

.event-count {
  font-size: 11px;
  color: #70757a;
}

.day-events {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.event-pill {
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.16);
  transition: filter 0.15s;
}

.event-pill:hover {
  filter: brightness(0.95);
}

.more-link {
  cursor: pointer;
  padding: 0 4px;
  font-size: 11px;
  font-weight: 500;
  color: #1a73e8;
}

.more-link:hover {
  text-decoration: underline;
}
</style>

<style>
/* Global popover styles for month calendar view event list */
.calendar-day-popover {
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(60,64,67,0.2);
  border: 1px solid #dadce0;
  min-width: 260px;
  max-width: 320px;
  font-family: inherit;
}
.calendar-day-popover .popover-header {
  background-color: #fff;
  border-bottom: 1px solid #dadce0;
  padding: 10px 14px;
}
.calendar-day-popover .popover-body {
  padding: 10px 14px;
}
.popover-header-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.popover-date {
  font-size: 13px;
  font-weight: 500;
  color: #3c4043;
}
.popover-count {
  font-size: 11px;
  color: #70757a;
  font-weight: 400;
}
.popover-events-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 10px;
  padding-right: 4px;
}
/* Scrollbar for popover list */
.popover-events-list::-webkit-scrollbar {
  width: 4px;
}
.popover-events-list::-webkit-scrollbar-track {
  background: transparent;
}
.popover-events-list::-webkit-scrollbar-thumb {
  background: #dadce0;
  border-radius: 4px;
}
.popover-events-list::-webkit-scrollbar-thumb:hover {
  background: #70757a;
}
.popover-event-pill {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.16);
  transition: filter 0.15s;
}
.popover-event-pill:hover {
  filter: brightness(0.95);
}
.popover-footer {
  border-top: 1px solid #f1f3f4;
  padding-top: 8px;
}
</style>
